import { formatMillioSav } from '../lib/formaz.ts';

/** Egy bekezdés részei: a behelyettesített értékek félkövérek. */
export interface SzkriptResz { szoveg: string; felkover?: boolean }
export interface SzkriptBekezdes { cim?: string; reszek: SzkriptResz[]; /** Halvány, a hívónak szóló teendő, nem felolvasandó. */ instrukcio?: boolean }

export interface HivasSzkriptAdat {
  irodaNev: string;
  /** A hívó értékesítő neve; ha üres, a helyjelölő kerül a szövegbe. */
  ertekesitoNev: string | null;
  hely: string;
  /** Null: szakértői becslés, szám nélkül. */
  sav: { min: number; max: number } | null;
}

export const ERTEKESITO_HELYJELOLO = '[az Ön neve]';

const MAGANHANGZO = /^[aáeéiíoóöőuúüű]/i;

/** A határozott névelő a következő szó első betűje szerint; számmal vagy más jellel kezdődő szónál a(z). */
export function nevelo(kovetkezoSzo: string): 'a' | 'az' | 'a(z)' {
  const elso = kovetkezoSzo.trim().charAt(0);
  if (!elso || !/\p{L}/u.test(elso)) return 'a(z)';
  return MAGANHANGZO.test(elso) ? 'az' : 'a';
}

const r = (szoveg: string, felkover = false): SzkriptResz => (felkover ? { szoveg, felkover } : { szoveg });
const e = (szoveg: string) => r(szoveg, true);

/**
 * A hívásszkript bekezdései (az irodavezető szkriptje, szó szerint; csak a név, az irodanév és a cím kerül be).
 * A becslés módszere, a KSH és az MNB nem hangzik el benne; a sáv csak a hívónak szóló halvány tájékoztatás.
 */
export function hivasSzkript(a: HivasSzkriptAdat): SzkriptBekezdes[] {
  const ertekesito = a.ertekesitoNev?.trim() || ERTEKESITO_HELYJELOLO;
  const helyAlatti = [r(`${nevelo(a.hely)} `), e(a.hely), r(' alatti')];
  const t = (szoveg: string, cim?: string): SzkriptBekezdes => (cim ? { cim, reszek: [r(szoveg)] } : { reszek: [r(szoveg)] });
  const ins = (szoveg: string): SzkriptBekezdes => ({ instrukcio: true, reszek: [r(szoveg)] });
  return [
    ins(a.sav ? `Az online becslés: ${formatMillioSav(a.sav.min, a.sav.max)}.` : 'Ennél az ingatlannál az online becslés nem adott számot (szakértői becslés).'),

    { cim: '1. Bemutatkozás', reszek: [r('Jó napot kívánok! '), e(ertekesito), r(` vagyok, ${nevelo(a.irodaNev)} `), e(a.irodaNev), r(' irodától.')] },
    { reszek: [r('Ön nemrégiben online árbecslést kért '), ...helyAlatti, r(' ingatlanára az ingatlanertekmeghatarozas.hu oldalon.')] },
    t('Ezzel kapcsolatban keresem. Alkalmas most néhány perc?'),

    t('Megkérdezhetem, hogy milyen céllal szerette volna megtudni az ingatlana értékét?', '2. Az érdeklődés céljának feltárása'),
    t('Esetleg értékesítésben gondolkodik, vagy egyelőre csak tájékozódik a jelenlegi piaci árakról?'),

    t('Értem. Milyen időtávban tervezi az értékesítést?', 'A) Ha értékesíteni szeretne'),
    t('Van már elképzelése arról, milyen áron szeretné meghirdetni az ingatlant?'),
    t('Hirdeti már az ingatlant, vagy még az előkészítés szakaszában tart?'),

    t('Az online ár-sáv jó kiindulópont, de a tényleges piaci értéket számos olyan tényező befolyásolja, amit személyes megtekintés nélkül nem lehet pontosan meghatározni.', 'Átvezetés a személyes találkozóra'),
    t('Szívesen megnézném az ingatlanát, és átbeszélhetnénk, hogy a jelenlegi piaci helyzetben milyen áron és milyen feltételekkel lenne érdemes értékesíteni.'),
    t('Ez egy kötelezettségmentes konzultáció.'),
    t('Önnek a hét melyik napja lenne alkalmas?'),

    t('Értem. Az ár-sáv egy későbbi értékesítési döntéshez szükséges, vagy más oka van annak, hogy most szeretné megtudni az ingatlana értékét?', 'B) Ha még csak gondolkodik az eladáson'),
    ins('Ha az értékesítés belátható időn belül szóba jöhet:'),
    t('Ebben az esetben érdemes lehet már most felmérni az ingatlan reális piaci értékét, hogy amikor az eladás mellett dönt, pontos információk alapján tudjon tervezni.'),
    t('Szívesen megnézném az ingatlant, és átbeszélhetnénk a lehetőségeket.'),
    t('Önnek mikor lenne alkalmas egy rövid személyes találkozó?'),

    t('Értem, természetesen. Az online becslés egy tájékoztató jellegű értéktartományt mutat, amelyet az ingatlan egyedi adottságai jelentősen befolyásolhatnak.', 'C) Ha csak kíváncsi az értékre'),
    t('Megkérdezhetem, hogy az ingatlan esetleges értékesítése egyáltalán felmerült Önben, vagy kizárólag tájékozódni szeretett volna?'),
    ins('Ha nem tervezi az értékesítést:'),
    t('Rendben, köszönöm a visszajelzését. Amennyiben később mégis felmerül az értékesítés lehetősége, szívesen állok rendelkezésére.'),
    ins('Állítsa „Elveszett”-re, „Csak érdeklődött” okkal.'),

    t('Természetesen, ezt teljesen megértem.', '3. Ha az ügyfél saját maga szeretné értékesíteni'),
    t('Ettől függetlenül szívesen segítek meghatározni az ingatlan reális piaci értékét, és megosztok Önnel néhány hasznos információt a jelenlegi ingatlanpiacról.'),
    t('A személyes konzultáció semmilyen kötelezettséggel nem jár.'),
    t('Mikor lenne alkalmas Önnek, hogy megtekintsem az ingatlant?'),

    t('Akkor egyeztessünk egy időpontot, amikor személyesen is meg tudom tekinteni az ingatlant.', '4. Időpontegyeztetés'),
    t('A találkozón pontosítjuk a becsült értéket, és amennyiben az értékesítés aktuális, átbeszéljük a lehetséges értékesítési stratégiát is.'),
    t('Önnek inkább délelőtt vagy délután lenne megfelelő?'),
    ins('Ha megvan az időpont: állítsa a státuszt „Szemle egyeztetve”-re az időponttal.'),

    t('Teljesen érthető. Mikorra tervezi az esetleges értékesítést?', '5. Ha később lesz aktuális'),
    t('Hozzájárulna, hogy az Ön által megjelölt időszakban újra megkeressem, és átbeszéljük az akkori piaci lehetőségeket?'),
    ins('Állítsa „Később aktuális”-ra, a megadott hónappal.'),

    { cim: '6. Beszélgetés lezárása', instrukcio: true, reszek: [r('Ha sikerült időpontot egyeztetni:')] },
    { reszek: [r('Köszönöm szépen! Akkor [nap, időpont] találkozunk '), ...helyAlatti, r(' ingatlannál.')] },
    t('Örülök, hogy személyesen is megismerhetem, és bízom benne, hogy hasznos információkkal tudok segíteni.'),
    t('Viszontlátásra!'),
    ins('Ha nem sikerült időpontot egyeztetni:'),
    t('Köszönöm, hogy időt szánt rám. Amennyiben a későbbiekben kérdése merül fel az ingatlan értékével vagy értékesítésével kapcsolatban, szívesen állok rendelkezésére.'),
    t('További szép napot kívánok!'),
  ];
}

/** A bekezdés sima szövege (tesztekhez, másoláshoz). */
export function bekezdesSzoveg(b: SzkriptBekezdes): string {
  return b.reszek.map((x) => x.szoveg).join('');
}
